import { FormBuilder, Validators, FormGroup } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import {
  TaskResponse,
  defaultTaskColors,
  CreateTaskPayload,
  getTaskStatusOptions,
  TaskStatus,
  LoadTasksByIds,
  selectTaskById,
  ApproveTask,
  RejectTask,
  upgradeTaskStatus,
} from '@tokeet-frontend/tasks'
import * as fromRoot from '@tv3/store/state'
import * as moment from 'moment'
import { SearchService } from '@tv3/store/search/search.service'
import {
  Destroyable,
  isSomething,
  selectAllRentals,
  untilDestroy,
  Attachment,
  getAttachments,
  AttachmentService,
  selectAttachmentsById,
  addAttachmentsComplete,
  DataEntityType,
  ConfirmDialogService,
  Toaster,
  validateForm,
  distinctObjectUntilChangedByFields,
  notAllNumbersValidator,
  selectSomeOnce,
  selectSome,
  SaveForm,
} from '@tokeet-frontend/tv3-platform'
import * as lodash from 'lodash'
import { Component, Inject, OnInit, ViewChild } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { AddTask, AddTaskComplete, DeleteTask, UpdateTask } from '@tokeet-frontend/tasks'
import { UploadBoxOptions } from '@tokeet-frontend/tv3-platform'
import { AuthService } from '@tv3/services/auth.service'
import { filter, map, startWith, switchMap, take } from 'rxjs/operators'
import { Actions, ofType } from '@ngrx/effects'
import { isUpdatingTasks, selectTaskLists } from '@tokeet-frontend/tasks'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import { LoadInquiryDetails } from '@tv3/store/inquiry/inquiry.actions'
import { EntityAttributesComponent } from '@tokeet-frontend/custom-attrs'
import { AddEntityTags, SetEntityTags } from '@tokeet-frontend/tags'
import {
  createRecurringForm,
  getRecurringFormData,
  setRecurringFormData,
} from '@tv3/shared/recurring-form/recurring-form.component'
import {
  createTaskChecklistForm,
  getTaskChecklistFormData,
  setTaskChecklistFormData,
} from './task-checklist/task-checklist.component'
import * as R from 'ramda'
import { selectAllIncidents } from '@tokeet-frontend/incident'

const taskDateFormat = 'YYYY-MM-DD HH:mm:ss'

export enum TaskDataTabs {
  Details,
  Conversations,
  Recurring,
  InquiryDetails,
  History,
  Attributes,
  Checklist,
  VendorImages,
}

@Component({
  selector: 'app-add-task-dialog',
  templateUrl: './task-dialog.component.html',
  styleUrls: ['./task-dialog.component.scss'],
  host: {
    class: 'modal-content',
  },
})
export class TaskDialogComponent extends Destroyable implements OnInit {
  @ViewChild('attrs') attrs: EntityAttributesComponent
  isEdit = false
  tabs = TaskDataTabs
  activeTab = TaskDataTabs.Details

  uploadBoxOptions: UploadBoxOptions = {
    autoUpload: true,
    resetAfterFailed: true,
    maxSize: 1024 * 1024 * 10,
    maxUploads: 1,
    token: this.authService.token,
    allowedFileExtensions: ['.png', '.jpg', '.jpeg', '.pdf', '.doc', '.docx', 'xlsx'],
    guide: $localize`You can only upload one file that is 10MB or smaller, only PNG, JPG, JPEG, PDF, WORD and EXCEL file types are allowed`,
  }

  recurringForm = createRecurringForm(this.fb)
  checklistForm = createTaskChecklistForm(this.fb)

  defaultTaskList = $localize`My Tasks`

  form = this.fb.group({
    name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100), notAllNumbersValidator]],
    list: [''],
    user_ids: [[], [Validators.required]],
    inquiry_id: [],
    rental_id: [],
    incident_id: [],
    start: ['', [Validators.required]],
    due: ['', [Validators.required]],
    timezone: [moment.tz.guess()],
    color: [defaultTaskColors[0].color, [Validators.required]],
    tags: [[]],
    notes: [],
    recurrenceRule: this.recurringForm,
    status: [TaskStatus.Pending],
    repeat: [0],
  })

  minDateDefault: Date

  colors = defaultTaskColors.map((c) => c.color)
  rentals$ = this.store.pipe(select(selectAllRentals))
  incidents$ = this.store.pipe(select(selectAllIncidents))
  isUpdatingTasks$ = this.store.pipe(select(isUpdatingTasks))

  get bgColor() {
    return defaultTaskColors.find((c) => c.color === this.form.get('color').value)?.bg
  }

  task: TaskResponse

  _attachments: Attachment[] = []
  vendorImages: Attachment[] = []
  userFiles: Attachment[] = []
  set attachments(items: Attachment[]) {
    this._attachments = items
    this.vendorImages = items?.filter((a) => a.isImage && a.isVendorImage)
    this.userFiles = items?.filter((a) => !a.isVendorImage)
  }
  get attachments() {
    return this._attachments
  }

  statuses = getTaskStatusOptions(false)
  taskLists$ = this.store.pipe(select(selectTaskLists))

  filteredLists = this.form.get('list').valueChanges.pipe(
    startWith(''),
    switchMap((value) =>
      this.taskLists$.pipe(
        map((lists) => R.filter((n) => n.toLowerCase().includes(value), lists)),
        map((lists) =>
          R.sort((a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))(lists)
        )
      )
    )
  )

  constructor(
    protected fb: FormBuilder,
    protected searchService: SearchService,
    protected store: Store<fromRoot.State>,
    private actions: Actions<any>,
    private authService: AuthService,
    private confirm: ConfirmDialogService,
    private toaster: Toaster,
    private amplitudeService: AmplitudeService,
    private attachmentService: AttachmentService,
    public dialogRef: MatDialogRef<TaskDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { taskId?: string; defaults?: Partial<CreateTaskPayload>; tab?: TaskDataTabs }
  ) {
    super()
    this.activeTab = this.data.tab || TaskDataTabs.Details
    this.isEdit = !!this.data.taskId
    if (!this.isEdit) {
      this.minDateDefault = new Date()
    } else {
      this.store.dispatch(LoadTasksByIds({ ids: [this.data.taskId] }))
    }
  }

  ngOnInit() {
    if (this.isEdit) {
      // this.form.disable()
      const task$ = this.store.pipe(select(selectTaskById(this.data.taskId)), filter(isSomething), untilDestroy(this))
      task$.pipe(take(1)).subscribe((task) => {
        this.task = task
        this.store.dispatch(getAttachments({ ids: task.attachments || [] }))
        this.setFormData({ ...task, status: upgradeTaskStatus(task, true) })
      })

      task$
        .pipe(switchMap((task) => this.store.pipe(select(selectAttachmentsById(task.attachments || [])))))
        .subscribe((attachments) => {
          this.attachments = attachments
        })
      task$
        .pipe(
          filter((t) => !!t.inquiry_id),
          distinctObjectUntilChangedByFields(['inquiry_id'])
        )
        .subscribe((task) => {
          this.store.dispatch(LoadInquiryDetails({ id: task.inquiry_id }))
        })
    } else if (isSomething(this.data.defaults)) {
      this.setFormData(this.data.defaults)
      const attachments = this.data.defaults.attachments
      if (isSomething(attachments)) {
        this.store.dispatch(getAttachments({ ids: attachments || [] }))
        this.store
          .pipe(selectSomeOnce(selectAttachmentsById(attachments || [])), untilDestroy(this))
          .subscribe((attachments) => (this.attachments = attachments))
      }
    }

    this.actions.pipe(ofType(AddTaskComplete), untilDestroy(this)).subscribe(({ task }) => {
      const { tags } = this.form.getRawValue()
      if (isSomething(tags)) {
        this.store.dispatch(AddEntityTags({ entityType: DataEntityType.Task, entityId: task.pkey, tags }))
      }
      this.close()
    })
  }

  close() {
    this.dialogRef.close()
  }

  private getTaskTimezone(timezone?: string) {
    return timezone && moment.tz.zone(timezone) ? timezone : moment.tz.guess()
  }

  private asTaskDate(epoch: number, timezone?: string) {
    if (!epoch) return undefined

    const zonedDate = moment.unix(epoch).tz(this.getTaskTimezone(timezone)).format(taskDateFormat)

    return moment(zonedDate, taskDateFormat).toDate()
  }

  private asTaskEpoch(date: Date, timezone?: string) {
    if (!date) return undefined

    return moment
      .tz(
        {
          year: date.getFullYear(),
          month: date.getMonth(),
          date: date.getDate(),
          hour: date.getHours(),
          minute: date.getMinutes(),
          second: date.getSeconds(),
        },
        this.getTaskTimezone(timezone)
      )
      .unix()
  }

  setFormData(task: Partial<TaskResponse>) {
    this.form.patchValue({
      ...task,
      color: task.color || defaultTaskColors[0].color,
      start: this.asTaskDate(task.start || task.created, task.timezone),
      due: this.asTaskDate(task.due, task.timezone),
    })
    setRecurringFormData(this.recurringForm, task.recurrenceRule)
    setTaskChecklistFormData(this.fb, this.checklistForm, task)
  }

  onSelectInquiry(item: Inquiry) {
    this.form.get('rental_id').setValue(item.rentalId, { emitEvent: false })
    this.form.get('inquiry_id').setValue(item.id, { emitEvent: false })
  }

  onUploadComplete(data: any[]) {
    const attachments = lodash.map(data, (d) => Attachment.deserialize(d))
    this.store.dispatch(addAttachmentsComplete({ attachments }))
    this.attachments = [...this.attachments, ...attachments]
    if (this.isEdit) {
      this.store.dispatch(
        UpdateTask({
          id: this.task.pkey,
          form: { attachments: this.attachments.map((t) => t.id) },
        })
      )
    }
  }

  onDownloadAttachment(item: Attachment) {
    this.attachmentService.downloadFile(item).subscribe()
  }

  onDeleteAttachment(item: Attachment) {
    this.confirm.confirm().subscribe(() => {
      this.attachments = lodash.filter(this.attachments, (i) => i.id !== item.id)
      if (this.task?.pkey) {
        this.store.dispatch(
          UpdateTask({
            id: this.task.pkey,
            form: { attachments: this.attachments.map((t) => t.id) },
          })
        )
      }
    })
  }

  onDelete() {
    this.confirm
      .confirm({
        title: $localize`Delete this task?`,
        body: $localize`Are you sure you want to delete this task?`,
      })
      .subscribe(() => {
        this.store.dispatch(DeleteTask({ id: this.task.pkey }))
        this.close()
      })
  }

  create() {
    const payload = this.form.getRawValue()
    this.store.dispatch(
      AddTask({
        form: {
          ...payload,
          start: this.asTaskEpoch(payload.start, payload.timezone),
          due: this.asTaskEpoch(payload.due, payload.timezone),
          attachments: this.attachments.map((t) => t.id),
          recurrenceRule: getRecurringFormData(this.recurringForm),
          checklist: getTaskChecklistFormData(this.checklistForm),
        },
      })
    )
    this.amplitudeService.logEvent('add-task')
  }

  update() {
    const { tags, ...payload } = this.form.getRawValue()
    this.store.dispatch(
      UpdateTask({
        form: {
          ...payload,
          start: this.asTaskEpoch(payload.start, payload.timezone),
          due: this.asTaskEpoch(payload.due, payload.timezone),
          attachments: this.attachments.map((t) => t.id),
          recurrenceRule: getRecurringFormData(this.recurringForm),
          checklist: getTaskChecklistFormData(this.checklistForm),
        },
        id: this.task.pkey,
      })
    )
    this.store.dispatch(SetEntityTags({ entityType: DataEntityType.Task, entityId: this.task.pkey, tags }))
    this.close()
  }

  @SaveForm()
  onApprove(form: FormGroup) {
    this.store.dispatch(ApproveTask({ taskId: this.task.pkey }))
    this.close()
  }

  onReject() {
    this.store.dispatch(RejectTask({ taskId: this.task.pkey }))
    this.close()
  }

  onSave(form: FormGroup) {
    const isFormValid = validateForm(form, false)

    if (!isFormValid || form.invalid) {
      this.toaster.warning($localize`Please fill out all the required fields`, $localize`Invalid details`)
      this.activeTab = this.tabs.Details
      return
    }

    if (!this.validateSubforms()) return

    const { timezone } = form.getRawValue()

    const doSave = () => {
      if (this.isEdit) {
        this.update()
      } else {
        this.create()
      }
    }

    if (!this.isEdit || !this.task?.timezone || timezone === this.task?.timezone) {
      doSave()
    } else {
      this.confirm
        .confirm({
          title: $localize`Warning!`,
          body: $localize`Your current TimeZone is` + ` ${moment.tz.guess()}. ` + $localize`Do you want to Proceed?`,
        })
        .subscribe(() => {
          doSave()
        })
    }
  }

  onSaveAttrs() {
    this.attrs.save()
    this.close()
  }

  validateSubforms() {
    this.checklistForm.markAllAsTouched()

    if (this.checklistForm.invalid) {
      this.toaster.warning($localize`Please check checklist data and correct.`, $localize`Invalid checklist data`)
      this.activeTab = this.tabs.Checklist
      return false
    }

    return true
  }
}
